import Box from '@mui/material/Box'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import { BELL_BADGE_SIZE } from './notificationCenterConstants'
import { bodyFor, notificationFocusUrl } from './notificationCenterViews'
import NotificationSeverityBadge from './NotificationSeverityBadge'
import NotificationSummary from './NotificationSummary'
import { useNotificationFacts } from './useNotificationFacts'

import type { Notification } from 'types/modules/notification-center/notification'

type NotificationToastProps = {
  notification: Notification
  onDone: () => void
}

/**
 * A notification arriving as a toast: the same summary the popover's rows show, so one event doesn't look
 * like two different things depending on where it is caught. The tinted surface and severity rail come
 * from the toast itself — see `NotificationToastStyles`. Clicking it goes where the popover's rows go.
 */
const NotificationToast = ({ notification, onDone }: NotificationToastProps) => {
  const navigate = useNavigate()

  const facts = useNotificationFacts(notification)

  // Same destination as the popover's rows: the inbox, scrolled to this notification with it open.
  const handleClick = useCallback(() => {
    navigate(notificationFocusUrl(notification.id))
    onDone()
  }, [navigate, notification.id, onDone])

  return (
    <Box sx={{ cursor: 'pointer', display: 'flex', width: '100%' }} onClick={handleClick}>
      <NotificationSummary
        badge={<NotificationSeverityBadge notification={notification} size={BELL_BADGE_SIZE} />}
        description={bodyFor(notification)}
        facts={facts}
        occurredAt={notification.occurredAt}
        title={notification.title}
      />
    </Box>
  )
}

export default NotificationToast
